/**
 * Typed client for Dev Mode QA-surface verdicts.
 *
 * Background: the operator web app's "Dev Mode" lets a platform admin walk
 * every QA surface (docs/qa/<kind>/<slug>.md) that isn't green yet and mark it
 * "verified" or "needs_fixing". A deployed SPA can't write back to the git
 * markdown, so the in-app verdict is persisted in core.qa_surface_verdicts
 * (keyed by checklist slug). The markdown checklists remain the git source of
 * truth; reconciling verdicts back into markdown status/[x] is a separate
 * agent/CI step.
 *
 *   * supabase/migrations/20260702140000_qa_surface_verdicts.sql
 *   * apps/web/src/devmode/*
 *
 * Reads go through core.v_qa_surface_verdicts (RLS-gated to platform admins;
 * non-admins get an empty list). Writes go through two security-definer RPCs
 * that re-check is_platform_admin().
 */

import { supabase } from '../client';

export type QaSurfaceVerdict = 'verified' | 'needs_fixing';

export type QaSurfaceKind =
  | 'modules'
  | 'integrations'
  | 'edge-functions'
  | 'cross-cutting';

/** One row from core.v_qa_surface_verdicts. */
export interface QaSurfaceVerdictRow {
  checklist_slug: string;
  checklist_kind: QaSurfaceKind;
  verdict: QaSurfaceVerdict;
  note: string | null;
  set_by: string | null;
  set_at: string;
  set_commit: string | null;
}

/**
 * Returns every in-app verdict. RLS-gated to platform admins -- non-admin
 * callers get an empty list silently, matching the rest of the admin-only
 * read surfaces in this repo.
 */
export async function listQaSurfaceVerdicts(): Promise<QaSurfaceVerdictRow[]> {
  const { data, error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .from('v_qa_surface_verdicts' as any)
    .select('checklist_slug,checklist_kind,verdict,note,set_by,set_at,set_commit')
    .order('checklist_slug', { ascending: true });
  if (error) throw error;
  return ((data ?? []) as unknown as QaSurfaceVerdictRow[]) ?? [];
}

/**
 * Upsert a verdict for a QA surface. Platform-admin only (the RPC raises
 * sqlstate 42501 otherwise). Returns the persisted row.
 */
export async function setQaSurfaceVerdict(input: {
  slug: string;
  kind: QaSurfaceKind;
  verdict: QaSurfaceVerdict;
  note?: string | null;
}): Promise<QaSurfaceVerdictRow> {
  const { data, error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc('rpc_qa_set_surface_verdict' as any, {
      p_slug: input.slug,
      p_kind: input.kind,
      p_verdict: input.verdict,
      p_note: input.note ?? null,
    });
  if (error) throw error;
  return data as unknown as QaSurfaceVerdictRow;
}

/**
 * Clear the verdict for a QA surface. Platform-admin only.
 */
export async function clearQaSurfaceVerdict(slug: string): Promise<void> {
  const { error } = await supabase()
    .schema('core')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .rpc('rpc_qa_clear_surface_verdict' as any, { p_slug: slug });
  if (error) throw error;
}
